import { useRef } from 'react'
import type { WireArticleBlock } from '../../../api/types'
import { useArticleArtifacts } from '../common/ArticleArtifactsContext'
import { useVisualEngagementContext } from '../common/VisualEngagementContext'
import { emitVisualInteraction, useVisualInViewTracking } from '../common/visualCollectionTracking'
import { PortionMark } from '../common/PortionMark'

type Block = Extract<WireArticleBlock, { type: 'audio' }>

export function AudioBlock({ block }: { block: Block }) {
  const artifacts = useArticleArtifacts()
  const engagement = useVisualEngagementContext()
  const figureRef = useRef<HTMLElement>(null)

  const visualId = block.artifactId || block.id
  const autoplay = block.autoplay ?? false

  useVisualInViewTracking(engagement, figureRef, visualId, 'AUDIO')

  const audioArtifact = artifacts.find(a => a.id === block.artifactId && a.type === 'audio')
  const audioSrc = audioArtifact?.presignedUrl ?? ''
  const transcriptSrc = block.transcriptArtifactId
    ? (artifacts.find(a => a.id === block.transcriptArtifactId)?.presignedUrl ?? undefined)
    : undefined

  return (
    <figure ref={figureRef} className="my-0 mx-auto max-w-3xl">
      {block.title && (
        <p className="mb-1 text-sm font-semibold text-fg">{block.title}</p>
      )}
      {(audioArtifact?.classification || (audioArtifact?.disseminationControls?.length ?? 0) > 0) && (
        <p className="mb-1.5 text-xs text-fg-secondary">
          Audio is classified as{' '}
          <PortionMark
            classification={audioArtifact?.classification}
            disseminationControls={audioArtifact?.disseminationControls}
          />
        </p>
      )}
      <audio
        src={audioSrc}
        controls
        autoPlay={autoplay}
        preload="metadata"
        aria-label={block.altText}
        className="w-full"
        onPointerEnter={() => emitVisualInteraction(engagement, visualId, 'AUDIO', 'HOVER_OVER')}
        onClick={() => emitVisualInteraction(engagement, visualId, 'AUDIO', 'CLICK')}
        onPlay={() => emitVisualInteraction(engagement, visualId, 'AUDIO', 'PLAY')}
        onPause={() => emitVisualInteraction(engagement, visualId, 'AUDIO', 'PAUSE')}
        onEnded={() => emitVisualInteraction(engagement, visualId, 'AUDIO', 'COMPLETE')}
        onSeeked={() => emitVisualInteraction(engagement, visualId, 'AUDIO', 'SEEK')}
      >
        {transcriptSrc && (
          <track kind="captions" src={transcriptSrc} srcLang="en" default />
        )}
      </audio>
      {(block.caption || block.credit) && (
        <figcaption className="mt-1.5 flex flex-wrap items-center gap-x-1.5 text-[11px] text-fg-secondary">
          {block.credit && (
            <>
              <span className="italic opacity-70">{block.credit}</span>
              {block.caption && <span aria-hidden="true">·</span>}
            </>
          )}
          {block.caption && (
            <>
              <PortionMark
                classification={block.captionClassification}
                disseminationControls={block.captionDisseminationControls}
              />
              {block.caption}
            </>
          )}
        </figcaption>
      )}
    </figure>
  )
}
